import { httpClient } from '../httpClient';
import {
  IEpisodeScript,
  CreateEpisodeScriptRequest,
  GenerateEpisodeScriptRequest,
  EpisodeScriptGenerationStatusResponse,
  ScriptCharacter,
  ScriptProp,
  ScriptScene,
  UpdateEpisodeScriptShotRequest,
  ScriptStep,
  StepData,
  RegenerateStepResponse,
  CreateEpisodeWithScriptRequest,
  CreateEpisodeWithScriptResponse,
  StartGenerationRequest,
  StartGenerationResponse
} from '@packages/global';

/**
 * 用户EpisodeScript API 模块
 * 处理剧本生成相关操作
 */
export class EpisodeScriptApi {
  // ============================================
  // 新版创建流程 API（推荐使用）
  // ============================================

  /**
   * 创建 Episode 和 EpisodeScript（不启动生成）
   * 
   * 新版创建流程的第一步：快速创建记录并导航到生成页面
   * 创建后 EpisodeScript.generationStatus 为 IDLE
   * 
   * @param data 创建请求参数
   * @returns 创建的 Episode、EpisodeScript 和 Project ID
   */
  async createEpisode(data: CreateEpisodeWithScriptRequest): Promise<CreateEpisodeWithScriptResponse> {
    return httpClient.post('/user/episode-scripts/create-episode', data);
  }

  /**
   * 启动剧本生成（从 Step 1 开始）
   * 
   * 新版创建流程的第二步：在生成页面自动调用，启动完整生成流程
   * 要求 EpisodeScript.generationStatus 为 IDLE
   * 
   * @param scriptId 剧本ID
   * @param data 可选的启动参数
   * @returns 任务ID和生成状态
   */
  async startGeneration(scriptId: string, data?: StartGenerationRequest): Promise<StartGenerationResponse> {
    return httpClient.post(`/user/episode-scripts/${scriptId}/start-generation`, data || {});
  }

  // ============================================
  // 旧版创建流程 API（已废弃）
  // ============================================

  /**
   * 创建准备任务
   * @deprecated 准备阶段已移除，请使用 createEpisode + startGeneration 替代
   */
  async createPreparationTask(data: GenerateEpisodeScriptRequest): Promise<{
    jobId: string;
    status: string;
    message: string;
  }> {
    return httpClient.post('/user/episode-scripts/prepare', data);
  }

  /**
   * 获取准备任务状态
   * @deprecated 准备阶段已移除，请使用 getGenerationStatus 替代
   */
  async getPreparationStatus(jobId: string): Promise<{
    jobId: string;
    status: string;
    progress: number;
    scriptId?: string;
    error?: string;
    createdAt: Date;
    updatedAt: Date;
  }> {
    return httpClient.get(`/user/episode-scripts/prepare/${jobId}`);
  }

  /**
   * 创建并启动剧本生成（一体化接口）
   * @deprecated 请使用 createEpisode + startGeneration 替代，新流程支持更好的用户体验
   */
  async createAndGenerate(data: GenerateEpisodeScriptRequest): Promise<{
    jobId: string;
    status: string;
    message: string;
  }> {
    return httpClient.post('/user/episode-scripts/create-and-generate', data);
  }

  // ============================================
  // 生成控制 API
  // ============================================

  /**
   * 重新生成剧本（基于现有剧本创建新版本并启动）
   */
  async retryGeneration(scriptId: string): Promise<{
    scriptId: string;
    taskId: string;
    generationStatus: string;
    message: string;
  }> {
    return httpClient.post(`/user/episode-scripts/${scriptId}/retry`, {});
  }

  /**
   * 强制重试剧本生成（用于卡住的任务）
   */
  async forceRetryGeneration(scriptId: string): Promise<{
    scriptId: string;
    taskId: string;
    generationStatus: string;
    message: string;
  }> {
    return httpClient.post(`/user/episode-scripts/${scriptId}/retry?force=true`, {});
  }

  /**
   * 重做指定步骤（适用于剧本已 COMPLETED 但需要单独重做某步）
   * @param scriptId   剧本 ID
   * @param stepNumber 要重做的步骤号（0-6）
   */
  async redoStep(scriptId: string, stepNumber: number): Promise<{
    scriptId: string;
    taskId: string;
    generationStatus: string;
    stepNumber: number;
    message: string;
  }> {
    return httpClient.post(`/user/episode-scripts/${scriptId}/redo-step/${stepNumber}`, {});
  }

  /**
   * 重新生成剧本（基于参考剧本创建新版本）
   */
  async renewGeneration(data: GenerateEpisodeScriptRequest, refScriptId: string): Promise<{
    jobId: string;
    status: string;
    message: string;
  }> {
    return httpClient.post(`/user/episode-scripts/renew-and-generate?refScriptId=${refScriptId}`, data);
  }

  /**
   * 查询生成状态
   */
  async getGenerationStatus(scriptId: string): Promise<EpisodeScriptGenerationStatusResponse> {
    return httpClient.get<EpisodeScriptGenerationStatusResponse>(`/user/episode-scripts/${scriptId}/status`);
  }

  /**
   * 取消生成任务
   */
  async cancelGeneration(scriptId: string): Promise<{
    scriptId: string;
    cancelled: boolean;
    generationStatus: string;
  }> {
    return httpClient.post(`/user/episode-scripts/${scriptId}/cancel`, {});
  }

  /**
   * 获取剧本详情
   */
  async getEpisodeScript(id: string): Promise<IEpisodeScript> {
    return httpClient.get<IEpisodeScript>(`/user/episode-scripts/${id}`);
  }

  /**
   * 获取Episode的所有剧本版本
   */
  async getEpisodeScripts(episodeId: string): Promise<IEpisodeScript[]> {
    return httpClient.get<IEpisodeScript[]>(`/user/episode-scripts/episode/${episodeId}`);
  }

  /**
   * 更新旁白音色
   */
  async updateNarratorVoice(scriptId: string, narratorVoiceUrl: string): Promise<IEpisodeScript> {
    return httpClient.patch<IEpisodeScript>(`/user/episode-scripts/${scriptId}/narrator-voice`, {
      narratorVoiceUrl
    });
  }

  /**
   * 更新背景音乐
   */
  async updateBgm(scriptId: string, bgmUrl: string): Promise<IEpisodeScript> {
    return httpClient.patch<IEpisodeScript>(`/user/episode-scripts/${scriptId}/bgm`, {
      bgmUrl
    });
  }

  /**
   * 通用更新接口 - 更新剧本的部分字段
   */
  async updateEpisodeScript(scriptId: string, updates: Partial<Pick<IEpisodeScript,
    // Step 1: 故事概要
    'title' | 'summary' | 'highlights' | 'language' |
    // Step 2: 视觉风格
    'styleName' | 'artStyle' | 'basePrompt' | 'bgmId' | 'bgmUrl' | 'narratorVoiceUrl' |
    // Step 3-6: 角色、道具、场景、分镜
    'characters' | 'props' | 'scenes' | 'shots'
  >>): Promise<IEpisodeScript> {
    return httpClient.patch<IEpisodeScript>(`/user/episode-scripts/${scriptId}`, updates);
  }

  /**
   * 更新角色信息
   */
  async updateCharacter(scriptId: string, characterName: string, updates: Partial<ScriptCharacter>): Promise<IEpisodeScript> {
    return httpClient.patch<IEpisodeScript>(`/user/episode-scripts/${scriptId}/character/${encodeURIComponent(characterName)}`, updates);
  }

  /**
   * 更新道具信息
   */
  async updateProp(scriptId: string, propName: string, updates: Partial<ScriptProp>): Promise<IEpisodeScript> {
    return httpClient.patch<IEpisodeScript>(`/user/episode-scripts/${scriptId}/prop/${encodeURIComponent(propName)}`, updates);
  }

  /**
   * 更新场景信息
   */
  async updateScene(scriptId: string, sceneName: string, updates: Partial<ScriptScene>): Promise<IEpisodeScript> {
    return httpClient.patch<IEpisodeScript>(`/user/episode-scripts/${scriptId}/scene/${encodeURIComponent(sceneName)}`, updates);
  }

  /**
   * 更新分镜信息
   */
  async updateShot(scriptId: string, shotIndex: number, updates: UpdateEpisodeScriptShotRequest, allShots: any[]): Promise<IEpisodeScript> {
    const updatedShots = allShots.map((shot, index) =>
      index === shotIndex ? { ...shot, ...updates } : shot
    );
    return this.updateEpisodeScript(scriptId, { shots: updatedShots });
  }

  /**
   * 删除分镜
   */
  async deleteShot(scriptId: string, shotIndex: number): Promise<IEpisodeScript> {
    return httpClient.delete<IEpisodeScript>(`/user/episode-scripts/${scriptId}/shot/${shotIndex}`);
  }

  /**
   * 删除角色
   */
  async deleteCharacter(scriptId: string, characterName: string): Promise<IEpisodeScript> {
    return httpClient.delete<IEpisodeScript>(`/user/episode-scripts/${scriptId}/character/${encodeURIComponent(characterName)}`);
  }

  /**
   * 删除道具
   */
  async deleteProp(scriptId: string, propName: string): Promise<IEpisodeScript> {
    return httpClient.delete<IEpisodeScript>(`/user/episode-scripts/${scriptId}/prop/${encodeURIComponent(propName)}`);
  }

  /**
   * 删除场景
   */
  async deleteScene(scriptId: string, sceneName: string): Promise<IEpisodeScript> {
    return httpClient.delete<IEpisodeScript>(`/user/episode-scripts/${scriptId}/scene/${encodeURIComponent(sceneName)}`);
  }

  /**
   * 删除剧本版本
   * 限制：不能删除正在使用的剧本（activeScriptId），不能删除最后一个剧本
   */
  async deleteEpisodeScript(scriptId: string): Promise<{ deleted: boolean }> {
    return httpClient.delete<{ deleted: boolean }>(`/user/episode-scripts/${scriptId}`);
  }

  // ============================================
  // 步骤相关 API (分步骤生成流程)
  // ============================================

  /**
   * 获取所有步骤状态
   * GET /api/user/episode-scripts/:id/steps
   */
  async getSteps(scriptId: string): Promise<ScriptStep[]> {
    return httpClient.get<ScriptStep[]>(`/user/episode-scripts/${scriptId}/steps`);
  }

  /**
   * 获取单个步骤数据
   * GET /api/user/episode-scripts/:id/steps/:stepNumber
   */
  async getStepData(scriptId: string, stepNumber: number): Promise<StepData> {
    return httpClient.get<StepData>(`/user/episode-scripts/${scriptId}/steps/${stepNumber}`);
  }

  /**
   * 更新步骤数据
   * PATCH /api/user/episode-scripts/:id/steps/:stepNumber
   */
  async updateStepData(scriptId: string, stepNumber: number, data: Partial<StepData>): Promise<StepData> {
    return httpClient.patch<StepData>(`/user/episode-scripts/${scriptId}/steps/${stepNumber}`, data);
  }

  /**
   * 重新生成步骤
   * POST /api/user/episode-scripts/:id/steps/:stepNumber/regenerate
   */
  async regenerateStep(
    scriptId: string,
    stepNumber: number,
    options?: { prompt?: string; preserveDownstream?: boolean }
  ): Promise<RegenerateStepResponse> {
    return httpClient.post<RegenerateStepResponse>(
      `/user/episode-scripts/${scriptId}/steps/${stepNumber}/regenerate`,
      options || {}
    );
  }
}

// 导出单例实例
export const episodeScriptApi = new EpisodeScriptApi();
